import { useRouter } from '@tanstack/react-router';
import { PageLoader } from '@customerio/pluma-components/react';
import Journeys from '@/pages/journeys';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import { MigrationRouteView } from '@/services/router/migration-route-view';
import { SafeNavigate } from '@/services/router/safe-navigate';
import { useMigrationHandoffHref } from '@/services/router/use-migration-handoff-href';
import { useCurrentWorkspace } from '@/context/current-workspace';
import { ProtectedRoute } from '@/services/router/components/protected-route';
import { generatePath } from '@/services/router/generate-path';
import PeopleListPage from './index.page';
import PeopleCreatePage from './create/index.page';

const LOADING_LABEL = 'Loading…';

/** The migrated URLs a `journeys` twin can forward to. */
type MigratedTarget = '/workspaces/$workspaceId/profiles' | '/workspaces/$workspaceId/profiles/create';

/**
 * Shared body of the two `journeys` handoffs below. Flag off → render the
 * embedded Ember host. Flag on → Hydra claims the URL purely to forward it to
 * `migratedTarget`, so Ember-side links keep working without being re-pointed.
 */
function useJourneysHandoff(migratedTarget: MigratedTarget) {
  const isMigrated = useFeatureFlag(AccountFeatureFlag.ProfilesMigration);
  const router = useRouter();
  const to = useMigrationHandoffHref(
    (workspaceId) => router.buildLocation({ to: migratedTarget, params: { workspaceId } }).href,
  );

  if (!isMigrated) {
    return <Journeys />;
  }

  if (to == null) {
    return <PageLoader label={LOADING_LABEL} />;
  }

  return <SafeNavigate to={to} options={{ shouldReplaceHistoryEntry: true }} />;
}

/**
 * The old `/journeys/people` and `/journeys/profiles` URLs (the Ember router
 * flag-swaps which segment is canonical per account).
 */
export function JourneysPeopleHandoff() {
  return useJourneysHandoff('/workspaces/$workspaceId/profiles');
}

/**
 * The migrated `/profiles` URL. Flag on → Hydra owns the page. Flag off → this
 * URL has no Ember counterpart (Ember only knows the `journeys` forms), so
 * hand back to the `journeys` twin, which Ember serves (canonicalizing the
 * segment itself) — the exact inverse of `JourneysPeopleHandoff`. The two
 * can't loop: each fires in the opposite flag state.
 */
export function PeopleListRouteView() {
  const isMigrated = useFeatureFlag(AccountFeatureFlag.ProfilesMigration);
  const router = useRouter();
  const legacyHref = useMigrationHandoffHref(
    (workspaceId) =>
      router.buildLocation({ to: '/workspaces/$workspaceId/journeys/people', params: { workspaceId } }).href,
  );
  // Readiness gates on the resolved workspace, not the URL segment: the
  // page's reads go through `requireWorkspaceId`, which throws while the
  // workspaces list is still loading.
  const workspace = useCurrentWorkspace();

  return (
    <MigrationRouteView isMigrated={isMigrated} legacyHref={legacyHref} isReady={workspace != null}>
      <PeopleListPage />
    </MigrationRouteView>
  );
}

/**
 * The old `/journeys/people/create` and `/journeys/profiles/create` URLs, the
 * add-people twins of {@link JourneysPeopleHandoff}.
 */
export function JourneysPeopleCreateHandoff() {
  return useJourneysHandoff('/workspaces/$workspaceId/profiles/create');
}

/**
 * The migrated `/profiles/create` URL. Flag off it hands back to the `journeys`
 * twin that Ember serves, the inverse of {@link JourneysPeopleCreateHandoff}.
 *
 * Ember's `people.create` route gates on `journeys/people/create` through
 * `redirectIfNoPermission`. `ProtectedRoute` is the Hydra equivalent, sending a
 * denied viewer to the profiles list.
 */
export function PeopleCreateRouteView() {
  const isMigrated = useFeatureFlag(AccountFeatureFlag.ProfilesMigration);
  const router = useRouter();
  const legacyHref = useMigrationHandoffHref(
    (workspaceId) =>
      router.buildLocation({ to: '/workspaces/$workspaceId/journeys/people/create', params: { workspaceId } }).href,
  );
  const workspace = useCurrentWorkspace();

  return (
    <MigrationRouteView isMigrated={isMigrated} legacyHref={legacyHref} isReady={workspace != null}>
      {/*
        Redirect target is built from the resolved workspace rather than `ProtectedRoute`'s route
        params: `MigrationRouteView` already gated on that workspace above, and the `last` sentinel
        is only a fallback for the window before it resolves.
      */}
      <ProtectedRoute
        permission="journeys/people/create"
        redirectTo={generatePath('/workspaces/:workspaceId/profiles', { workspaceId: workspace?.id ?? 'last' })}
      >
        <PeopleCreatePage />
      </ProtectedRoute>
    </MigrationRouteView>
  );
}
